import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import { formatPriceMinor } from '../labels';
import type { PassTuple, PoolTone, RiverModel, RiverGate, TowerTarget } from './tower-model';
import { TONE } from './tower-tones';
import { useTweenMap } from './use-tween';

// THE RIVER — the Tower's hero: the disruption's PNR groups as a flow through six
// gates: Rebooking → Lodging → Acceptance → Issue (auto) → Transfers → Departure.
// The passenger accepts ONE offer (new flight + hotel when overnight, or flight
// only); after acceptance the ticket is issued and the hotel confirmed by the
// system; the issued groups with a hotel are moved there by shuttle (MASS ground
// transport) — the flight-only ones go around Transfers straight to departure.
// Hand-rolled SVG (no chart lib). Widths ∝ groups; what passes a gate keeps
// flowing right, what doesn't pools below it (clickable → the roster / hotels
// view). Fill language: SOLID GREEN = done / irreversible (ticketed · confirmed ·
// accepted · issued), BLUE HATCH = reversible (seat on HOLD / hotel HOLD / offer out), PALE AMBER =
// pending, GREY = legitimate bypass (“Flight only” around Lodging, “No hotel”
// around Transfers). Numbers tween
// on every WS push so the river visibly drains; the dashed “traffic” rides the
// ribbon INTO the gate a run is feeding (the Issue ribbon while commits are in
// flight). Facts unknown → hatched gate + “waiting”, never zeros.

const GW = 12;
const Y0 = 84;
const POOL_W = 124;
/** Gates with a pass count (0..4); Departure (5) ends in the pools on the right. */
const GATES = 6;
const RIBBONS = GATES - 1;

/**
 * The drawing is 1 unit = 1 CSS px: the viewBox IS the box (ResizeObserver), so
 * type, chips and pills keep the UI's sizes on any screen width, and the six
 * gates spread across the available width. The ribbon band takes ~38 % of the
 * height and the pools the rest — no dead space, no giant text on wide screens.
 */
function geometry(boxW: number, boxH: number) {
  const W = Math.max(560, Math.round(boxW));
  const H = Math.max(360, Math.round(boxH));
  // The entrance lane hosts one flight CARD per band (code · route · time · pax ·
  // groups · cost) — wide enough to read, never more than a sixth of the width.
  const leftPad = Math.max(140, Math.min(220, Math.round(W * 0.15)));
  const rightPad = 168; // the Departure pools (“222 · At the hotel · waiting to depart” must fit)
  const step = (W - leftPad - rightPad) / RIBBONS;
  const X = Array.from({ length: GATES }, (_, i) => Math.round(leftPad + i * step)) as unknown as readonly [
    number,
    number,
    number,
    number,
    number,
    number,
  ];
  const HMAX = Math.max(120, Math.min(340, Math.round(H * 0.38)));
  const POOL_Y = Y0 + HMAX + 40;
  const POOL_MAX = Math.max(30, Math.min(88, Math.round((H - POOL_Y - 44) / 3)));
  return { W, H, X, HMAX, POOL_Y, POOL_MAX };
}

interface TowerRiverProps {
  model: RiverModel;
  currency?: string | undefined;
  onTarget: (target: TowerTarget) => void;
  /** Selected flight (caseUrn) — its slice lights up, the rest of the river dims. */
  selectedCase?: string | null | undefined;
  onSelectCase?: ((caseUrn: string | null) => void) | undefined;
  /** Flight-card actions (shown on the selected band). */
  onOpenPassengers?: ((caseUrn: string) => void) | undefined;
  onOpenCase?: ((caseUrn: string) => void) | undefined;
  className?: string;
}

function fmt(n: number): string {
  return Math.round(n).toLocaleString();
}

function segPath(xa: number, xb: number, ya: number, ha: number, yb: number, hb: number): string {
  const xm = (xa + xb) / 2;
  return `M${xa},${ya} C${xm},${ya} ${xm},${yb} ${xb},${yb} L${xb},${yb + hb} C${xm},${yb + hb} ${xm},${ya + ha} ${xa},${ya + ha} Z`;
}
function spillPath(
  gx: number,
  yA: number,
  yB: number,
  px1: number,
  px2: number,
  py: number,
): string {
  return `M${gx},${yA} C${gx + 30},${yA} ${px1},${py - 34} ${px1},${py} L${px2},${py} C${px2},${py - 34} ${gx + 30},${yB} ${gx},${yB} Z`;
}

/** A small readable label chip drawn over a ribbon (1–2 lines, centred). */
function chip(
  key: string,
  cx: number,
  cy: number,
  lines: string[],
  maxW = 400,
  solidText = false,
): ReactElement {
  const longest = lines.reduce((m, l) => Math.max(m, l.length), 0);
  const natural = Math.max(44, longest * 5.9 + 14);
  // Fit inside the ribbon segment: shrink the type (never below ~8px) before clipping.
  const k = Math.min(1, Math.max(0.78, (maxW - 6) / natural));
  const w = Math.min(natural * k, maxW - 6);
  const f1 = 10.5 * k;
  const f2 = 9.5 * k;
  const h = lines.length > 1 ? 30 : 18;
  return (
    <g key={key} pointerEvents="none">
      <rect
        x={cx - w / 2}
        y={cy - h / 2}
        width={w}
        height={h}
        rx={4}
        fill={TONE.surface}
        opacity={0.92}
        stroke={TONE.line}
      />
      <text
        x={cx}
        y={lines.length > 1 ? cy - 4 : cy + 1}
        textAnchor="middle"
        fontSize={f1}
        fontWeight={600}
        fill={solidText ? TONE.text : 'hsl(var(--foreground) / 0.85)'}
        dominantBaseline="middle"
      >
        {lines[0]}
      </text>
      {lines[1] ? (
        <text
          x={cx}
          y={cy + 9}
          textAnchor="middle"
          fontSize={f2}
          fill={TONE.muted}
          dominantBaseline="middle"
        >
          {lines[1]}
        </text>
      ) : null}
    </g>
  );
}

function poolColors(tone: PoolTone): {
  fill: string;
  stroke: string;
  text: string;
  dashed?: boolean;
} {
  switch (tone) {
    case 'warn':
      return { fill: TONE.warnSoft, stroke: TONE.warn, text: TONE.warn };
    case 'danger':
      return { fill: TONE.dangerSoft, stroke: TONE.danger, text: TONE.danger };
    case 'info':
      return { fill: TONE.infoSoft, stroke: TONE.info, text: TONE.info };
    case 'byp':
      return { fill: 'hsl(var(--muted))', stroke: TONE.line, text: TONE.muted };
    case 'hatch':
      return { fill: TONE.surface, stroke: TONE.ink, text: TONE.text, dashed: true };
    case 'ok':
      return { fill: TONE.okSoft, stroke: TONE.ok, text: TONE.ok };
    case 'solid':
      return { fill: TONE.done, stroke: TONE.done, text: 'white' };
    default:
      return { fill: TONE.pendSoft, stroke: TONE.pend, text: TONE.text };
  }
}

function ledColor(led: RiverGate['led']): string {
  switch (led) {
    case 'ok':
      return TONE.ok;
    case 'run':
      return TONE.ink;
    case 'warn':
      return TONE.warn;
    case 'info':
      return TONE.info;
    default:
      return TONE.muted;
  }
}

const reduceMotion =
  typeof window !== 'undefined' &&
  !!window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function TowerRiver({
  model,
  currency = 'USD',
  onTarget,
  selectedCase = null,
  onSelectCase,
  onOpenPassengers,
  onOpenCase,
  className,
}: TowerRiverProps) {
  const { t } = useTranslation('tower');
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [box, setBox] = useState({ w: 720, h: 420 });
  useEffect(() => {
    const el = boxRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (r && r.width > 0 && r.height > 0) setBox({ w: r.width, h: r.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const { W, H, X, HMAX, POOL_Y, POOL_MAX } = geometry(box.w, box.h);
  // Flatten every drawn number so the tween can ease all of them together.
  const target = useMemo(() => {
    const t: Record<string, number> = {
      total: model.total,
      travelled: model.end.travelled,
      ready: model.end.ready,
      processed0: model.gates[0]?.processed ?? 0,
    };
    model.gates.forEach((g, i) => {
      t[`in${i}`] = g.inFlow;
      t[`out${i}`] = g.outFlow;
      t[`byp${i}`] = g.bypass;
      t[`sol${i}`] = g.outSolid ?? 0;
      g.pools.forEach((p) => {
        t[`p${i}:${p.key}`] = p.n;
      });
    });
    return t;
  }, [model]);
  const v = useTweenMap(target);
  const total = Math.max(1, v.total ?? model.total);
  const sc = HMAX / total;

  const els: ReactElement[] = [];
  const in0 = (v.in0 ?? 0) * sc;

  // entrance: the part the run already EVALUATED (ink) over the part still
  // pending (pale amber) — the river visibly changes colour left→right while the
  // engine works, before anything passes the gate.
  const done0 = Math.min(in0, (v.processed0 ?? 0) * sc);
  els.push(
    <path key="entrance-pend" d={segPath(0, X[0], Y0, in0, Y0, in0)} fill={TONE.pendSoft} />,
  );
  if (done0 > 0.5) {
    els.push(
      <path key="entrance-done" d={segPath(0, X[0], Y0, done0, Y0, done0)} fill={TONE.inkSoft} />,
    );
  }
  // Per-flight bands in the entrance lane: hairline-separated slices, in strip
  // order, each labelled with its flight when tall enough. Click = select the
  // flight (its slice lights up at every gate where we know its pass count; the
  // rest of the river dims). `approx` = pax-proportional until the fold has run.
  const bandEls: ReactElement[] = [];
  const bandOffsets = new Map<string, { y: number; h: number; passed: PassTuple }>();
  {
    const bands = model.bands;
    const sumGroups = bands.reduce((a, b) => a + b.groups, 0);
    const scaleB = sumGroups > 0 ? in0 / sumGroups : 0;
    let y = Y0;
    for (const b of bands) {
      const h = b.groups * scaleB;
      if (h <= 0) continue;
      bandOffsets.set(b.caseUrn, { y, h, passed: b.passed });
      const sel = selectedCase === b.caseUrn;
      const dim = selectedCase != null && !sel;
      // The flight CARD, drawn inside its slice — as many lines as the height allows.
      const cardText = sel ? 'hsl(var(--primary-foreground))' : dim ? TONE.muted : TONE.text;
      const cardMuted = sel ? 'hsl(var(--primary-foreground) / 0.8)' : TONE.muted;
      const line2 = `${b.route} · ${b.time}${b.departed ? t('river.band.departed') : ''}`;
      const line3 =
        t('river.band.groups', { n: fmt(b.groups) }) +
        (b.assigned != null ? t('river.band.withFlight', { count: b.assigned }) : '') +
        (b.noOptions ? t('river.band.noOptions', { count: b.noOptions }) : '');
      // airline-money@v2 — the flight's hotel spend at the airline's PRICE; an unknown
      // (null) price simply drops off the card rather than reading as $0.
      const cost =
        b.priceMinor != null && b.priceMinor > 0 ? formatPriceMinor(b.priceMinor, currency, t) : '';
      const stop = (e: { stopPropagation: () => void }) => e.stopPropagation();
      bandEls.push(
        <g
          key={`band-${b.key}`}
          className="cursor-pointer"
          onClick={() => onSelectCase?.(sel ? null : b.caseUrn)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) =>
            e.key === 'Enter' ? onSelectCase?.(sel ? null : b.caseUrn) : undefined
          }
        >
          <title>{`${t('river.band.title', { code: b.code, route: b.route, time: b.time, groups: fmt(b.groups), pax: fmt(b.pax) })}${cost ? ` · ${cost}` : ''}${b.approx ? t('river.band.approx') : ''}${sel ? t('river.band.clickClear') : t('river.band.clickHighlight')}`}</title>
          <rect
            x={0}
            y={y}
            width={X[0]}
            height={h}
            fill={sel ? TONE.ink : 'transparent'}
            opacity={sel ? 0.55 : 0}
          />
          {dim ? (
            <rect x={0} y={y} width={X[0]} height={h} fill={TONE.surface} opacity={0.6} />
          ) : null}
          {y > Y0 ? (
            <line x1={0} x2={X[0]} y1={y} y2={y} stroke={TONE.surface} strokeWidth={1.5} />
          ) : null}
          {h >= 13 ? (
            <text
              x={8}
              y={h >= 34 ? y + 14 : y + h / 2 + 1}
              fontSize={11}
              fontWeight={700}
              fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
              fill={cardText}
              dominantBaseline="middle"
            >
              {b.code}
              <tspan fontWeight={500} fill={cardMuted}>
                {t('river.paxSuffix', { n: fmt(b.pax) })}
              </tspan>
              {b.departed && h < 34 ? (
                <tspan fontWeight={500} fill={cardMuted}>{t('river.band.departed')}</tspan>
              ) : null}
            </text>
          ) : null}
          {h >= 34 ? (
            <text x={8} y={y + 28} fontSize={10.5} fill={cardMuted} dominantBaseline="middle">
              {line2}
            </text>
          ) : null}
          {h >= 50 ? (
            <text x={8} y={y + 42} fontSize={10.5} fill={cardMuted} dominantBaseline="middle">
              {line3}
              {cost ? <tspan fontWeight={600} fill={cardText}>{` · ${cost}`}</tspan> : null}
            </text>
          ) : null}
          {sel && h >= 66 && (onOpenPassengers || onOpenCase) ? (
            <text x={8} y={y + 58} fontSize={10.5} fontWeight={700} dominantBaseline="middle">
              {onOpenPassengers ? (
                <tspan
                  fill={cardText}
                  textDecoration="underline"
                  onClick={(e) => {
                    stop(e);
                    onOpenPassengers(b.caseUrn);
                  }}
                >
                  {t('river.band.passengers')}
                </tspan>
              ) : null}
              {onOpenPassengers && onOpenCase ? <tspan fill={cardMuted}>{'   '}</tspan> : null}
              {onOpenCase ? (
                <tspan
                  fill={cardText}
                  textDecoration="underline"
                  onClick={(e) => {
                    stop(e);
                    onOpenCase(b.caseUrn);
                  }}
                >
                  {t('river.band.openCase')}
                </tspan>
              ) : null}
            </text>
          ) : null}
        </g>,
      );
      y += h;
    }
  }
  const selBand = selectedCase ? bandOffsets.get(selectedCase) : undefined;
  // Everything downstream is dimmed while a flight is selected; its own slice is
  // re-drawn on top of each ribbon at the SAME stacked offset (a flight's groups
  // travel together), sized by how many of its groups passed that gate.
  const dimAll = selectedCase != null;
  const highlights: ReactElement[] = [];
  function sliceOffset(gateIdx: 0 | 1 | 2 | 3 | 4): { y: number; h: number } | null {
    if (!selBand) return null;
    // Offset = sum of the passes of the flights stacked ABOVE this one.
    let above = 0;
    let found = false;
    for (const b of model.bands) {
      if (b.caseUrn === selectedCase) {
        found = true;
        break;
      }
      above += b.passed[gateIdx];
    }
    if (!found) return null;
    return { y: Y0 + above * sc, h: selBand.passed[gateIdx] * sc };
  }
  // Labels ride on top of everything (pushed after gates) so the picture reads
  // itself: what each full ribbon IS and how many groups it carries.
  const labels: ReactElement[] = [];
  // With per-flight bands the lane labels itself (one flight code per slice) — the
  // whole-lane chip is only for the single-flight / no-bands case.
  if (in0 > 24 && model.bands.length <= 1) {
    const pend = Math.max(0, Math.round((v.in0 ?? 0) - (v.processed0 ?? 0)));
    const evaluated = Math.round(v.processed0 ?? 0);
    const paxIn = model.totalPax > 0 ? t('river.paxSuffix', { n: fmt(model.totalPax) }) : '';
    const entranceLine = `${t('river.entrance.groups', { n: fmt(v.in0 ?? 0) })}${paxIn}`;
    labels.push(
      chip(
        'lbl-entrance',
        X[0] / 2,
        Y0 + in0 / 2,
        pend > 0 && model.runningGate === 0
          ? [entranceLine, t('river.entrance.evaluated', { count: evaluated })]
          : pend > 0
            ? [entranceLine, t('river.entrance.pending', { count: pend })]
            : [entranceLine, t('river.entrance.affected')],
        X[0],
      ),
    );
  }
  if (model.runningGate === 0) {
    els.push(
      <path key="traffic0" d={segPath(0, X[0], Y0, in0, Y0, in0)} fill="url(#tower-traffic)" />,
    );
    els.push(
      <path
        key="dash0"
        className="tower-dash"
        d={`M0,${Y0 + in0 / 2} L${X[0]},${Y0 + in0 / 2}`}
        stroke={TONE.ink}
        strokeWidth={2}
        fill="none"
        strokeDasharray="8 8"
        opacity={0.7}
      />,
    );
  }

  const gateEls = model.gates.map((g, i) => {
    const x = X[i]!;
    const hIn = (v[`in${i}`] ?? 0) * sc;
    const hOut = (v[`out${i}`] ?? 0) * sc;
    const hSol = Math.min(hOut, (v[`sol${i}`] ?? 0) * sc);
    const dim = !g.known || (g.inFlow < 0.5 && g.outFlow < 0.5);
    const cx = x + GW / 2;
    const items: ReactElement[] = [];

    // The gate PILLAR — drawn on top of the ribbons and separated from them by a
    // surface-coloured stroke so it always reads as a gate, never as part of the
    // flow. Its filled part (what passed) is SOLID ink whatever the ribbon's fill,
    // so the pillar never merges with a hatched ribbon; dim gates are hatched grey.
    items.push(
      <rect
        key="bar"
        x={x}
        y={Y0 - 8}
        width={GW}
        height={HMAX + 16}
        rx={6}
        fill={dim ? 'url(#tower-hatch-grey)' : TONE.line}
        stroke={TONE.surface}
        strokeWidth={2}
      />,
    );
    if (hOut > 0.5) {
      items.push(
        <rect
          key="done"
          x={x + 2}
          y={Y0}
          width={GW - 4}
          height={hOut}
          rx={3}
          fill={g.outFill === 'hatch' && hSol < 0.5 ? TONE.ink : TONE.done}
          opacity={g.outFill === 'hatch' && hSol < 0.5 ? 0.55 : 1}
        />,
      );
      if (g.outFill === 'hatch' && hSol > 0.5)
        items.push(
          <rect key="sol" x={x + 2} y={Y0} width={GW - 4} height={hSol} rx={3} fill={TONE.done} />,
        );
    }
    // header
    items.push(
      <g
        key="hd"
        className="cursor-pointer"
        onClick={() => onTarget(gateTarget(g))}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === 'Enter' ? onTarget(gateTarget(g)) : undefined)}
      >
        <title>{`${t('river.gate.title', { name: g.name, count: g.headerCount })}${g.outPax != null && g.outPax > 0 ? t('river.gate.titlePax', { n: fmt(g.outPax) }) : ''}${g.headerSub ? ` · ${g.headerSub}` : ''}`}</title>
        <circle
          cx={cx - g.name.length * 3.4 - 10}
          cy={26}
          r={4}
          fill={ledColor(g.led)}
          className={g.led === 'run' ? 'tower-pulse' : undefined}
        />
        <text
          x={cx}
          y={30}
          textAnchor="middle"
          fontSize={12}
          fontWeight={600}
          fill={dim ? TONE.muted : TONE.text}
        >
          {g.name}
        </text>
        <text
          x={cx}
          y={46}
          textAnchor="middle"
          fontSize={11.5}
          fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
          fill={dim ? TONE.muted : 'hsl(var(--foreground) / 0.8)'}
        >
          {g.headerCount}
        </text>
        {g.headerSub ? (
          <text x={cx} y={60} textAnchor="middle" fontSize={10.5} fill={TONE.muted}>
            {g.headerSub}
          </text>
        ) : null}
        {g.headerSub2 ? (
          <text x={cx} y={72} textAnchor="middle" fontSize={10.5} fill={TONE.muted}>
            {g.headerSub2}
          </text>
        ) : null}
      </g>,
    );
    // pools
    let py = POOL_Y;
    let first = true;
    const px1 = x - 40;
    g.pools.forEach((p) => {
      const n = v[`p${i}:${p.key}`] ?? 0;
      if (n < 0.5) return;
      const h = Math.max(26, Math.min(POOL_MAX, n * sc * 0.9));
      const c = poolColors(p.tone);
      const w = POOL_W + Math.min(120, Math.max(0, p.label.length - 14) * 5.8);
      if (first) {
        items.push(
          <path
            key={`spill-${p.key}`}
            d={spillPath(x, Y0 + hOut, Y0 + hIn, px1, px1 + Math.min(w, 120), py)}
            fill={c.stroke}
            opacity={0.22}
          />,
        );
        first = false;
      }
      items.push(
        <g
          key={`pool-${p.key}`}
          className="cursor-pointer"
          onClick={() => p.target && onTarget(p.target)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => (e.key === 'Enter' && p.target ? onTarget(p.target) : undefined)}
        >
          <title>{`${t('river.pool.title', { n: fmt(n), label: p.label })}${p.target ? t('river.pool.open') : ''}`}</title>
          <rect
            x={px1}
            y={py}
            width={w}
            height={h}
            rx={6}
            fill={c.fill}
            stroke={c.stroke}
            strokeWidth={c.dashed ? 1.5 : 1}
            strokeDasharray={c.dashed ? '4 3' : undefined}
          />
          <text
            x={px1 + 10}
            y={py + h / 2 + 1}
            fontSize={13}
            fontWeight={700}
            fill={c.text}
            dominantBaseline="middle"
          >
            {fmt(n)}
          </text>
          <text
            x={px1 + 10 + fmt(n).length * 8.2 + 6}
            y={py + h / 2 + 1}
            fontSize={11}
            fill={p.tone === 'solid' ? c.text : 'hsl(var(--foreground) / 0.8)'}
            dominantBaseline="middle"
          >
            {p.label}
          </text>
          {p.target ? (
            <text
              x={px1 + w - 9}
              y={py + h / 2 + 1}
              fontSize={11}
              textAnchor="end"
              fill={TONE.muted}
              dominantBaseline="middle"
            >
              ›
            </text>
          ) : null}
        </g>,
      );
      py += h + 8;
    });
    return <g key={`gate-${i}`}>{items}</g>;
  });

  // ribbons between gates. Two gates have a BYPASS: Lodging (the groups with a
  // flight that need no hotel go AROUND it, grey band, and rejoin at the offer)
  // and Transfers (the issued flight-only groups go AROUND it straight to
  // departure). The split is drawn on the ribbon INTO the bypassed gate, the
  // ride-along + rejoin on the ribbon OUT of it.
  const ribbons: ReactElement[] = [];
  for (let i = 0; i < RIBBONS; i++) {
    const g = model.gates[i]!;
    const hOut = (v[`out${i}`] ?? 0) * sc;
    const hInNext = (v[`in${i + 1}`] ?? 0) * sc;
    const xa = X[i]! + GW;
    const xb = X[i + 1]!;
    // The grey band that goes around THIS gate (rejoins at i+1) / around the NEXT one.
    const hBypSelf = (v[`byp${i}`] ?? 0) * sc;
    const hBypNext = (v[`byp${i + 1}`] ?? 0) * sc;
    if (hOut < 0.5 && hBypSelf < 0.5) continue;
    {
      const sl = sliceOffset(i as 0 | 1 | 2 | 3 | 4);
      if (sl && sl.h > 0.5) {
        highlights.push(
          <path
            key={`hl-${i}`}
            d={segPath(xa, xb, sl.y, sl.h, sl.y, sl.h)}
            fill={
              g.outFill === 'hatch' && (v[`sol${i}`] ?? 0) < (v[`out${i}`] ?? 0) - 0.5
                ? TONE.ink
                : TONE.done
            }
            opacity={0.85}
            pointerEvents="none"
          />,
        );
      }
    }
    if (hOut >= 0.5) {
      // What this full ribbon means, in the operator's words.
      const n = fmt(v[`out${i}`] ?? 0);
      const sol = Math.round(v[`sol${i}`] ?? 0);
      const held = Math.max(0, Math.round((v[`out${i}`] ?? 0) - sol));
      // Groups AND the people behind them (a group is 1..n passengers).
      const pax = g.outPax != null && g.outPax > 0 ? t('river.paxSuffix', { n: fmt(g.outPax) }) : '';
      const lines =
        i === 0
          ? [
              `${t('river.ribbon.withFlight', { n })}${pax}`,
              g.outFill !== 'hatch' || (sol > 0 && held === 0)
                ? t('river.ribbon.ticketed')
                : sol > 0
                  ? t('river.ribbon.ticketedHold', { ticketed: sol, held })
                  : t('river.ribbon.seatsOnHold'),
            ]
          : i === 1
            ? sol > 0 && held > 0
              ? [
                  `${t('river.ribbon.hotelMixed', { confirmed: sol, held })}${pax}`,
                  t('river.ribbon.hotels'),
                ]
              : g.outFill === 'hatch'
                ? [
                    `${t('river.ribbon.hotelHold', { n })}${pax}`,
                    t('river.ribbon.untilAcceptance'),
                  ]
                : [
                    `${t('river.ribbon.hotelsConfirmed', { n })}${pax}`,
                    t('river.ribbon.irreversible'),
                  ]
            : i === 2
              ? [
                  `${t('river.ribbon.accepted', { n })}${pax}`,
                  t('river.ribbon.flightHotelIrreversible'),
                ]
              : i === 3
                ? [
                    `${t('river.ribbon.issued', { n })}${pax}`,
                    t('river.ribbon.ticketHotelConfirmed'),
                  ]
                : [
                    `${t('river.ribbon.atHotel', { n })}${pax}`,
                    t('river.ribbon.shuttleCompleted'),
                  ];
      const cxm = (xa + xb) / 2;
      const nextIn = hBypNext > 0.5 ? Math.max(0, Math.min(hOut, hInNext)) : hOut;
      const band = Math.min(hOut, nextIn);
      if (band >= 34) labels.push(chip(`lbl-${i}`, cxm, Y0 + band / 2, lines, xb - xa));
      else if (hOut >= 6 && (i <= 1 || i === 4))
        labels.push(chip(`lbl-${i}`, cxm, Y0 + hOut + 20, lines, xb - xa));
    }
    const fill = g.outFill === 'hatch' ? 'url(#tower-hatch)' : TONE.done;
    const hSol = Math.min(hOut, (v[`sol${i}`] ?? 0) * sc);
    if (hBypSelf > 0.5) {
      // OUT of a bypassed gate: the main flow continues; the bypass rides along the
      // pillar and rejoins UNDER the main flow into the next gate (its in-flow =
      // what passed + what went around).
      const hMain = Math.max(0, Math.min(hOut, hInNext));
      if (hOut > 0.5) {
        ribbons.push(<path key={`r${i}`} d={segPath(xa, xb, Y0, hOut, Y0, hMain)} fill={fill} />);
        if (g.outFill === 'hatch' && hSol > 0.5)
          ribbons.push(
            <path
              key={`r${i}s`}
              d={segPath(xa, xb, Y0, hSol, Y0, Math.min(hSol, hMain))}
              fill={TONE.done}
            />,
          );
      }
      const hJoin = Math.max(0, Math.min(hBypSelf, hInNext - hMain));
      ribbons.push(
        <path
          key={`r${i}b`}
          d={segPath(X[i]!, xb, Y0 + hOut, hBypSelf, Y0 + hMain, hJoin > 0.5 ? hJoin : hBypSelf)}
          fill={TONE.line}
        />,
      );
      const bx = (X[i]! + GW + xb) / 2;
      const by = Y0 + hOut + hBypSelf / 2 + (hInNext - hOut) / 4;
      if (hBypSelf > 26) {
        ribbons.push(
          <text
            key={`r${i}t`}
            x={bx}
            y={by - 4}
            textAnchor="middle"
            fontSize={11.5}
            fontWeight={600}
            fill="hsl(var(--foreground) / 0.8)"
          >
            {g.bypassLabel ?? t('river.bypass', { n: fmt(v[`byp${i}`] ?? 0) })}
          </text>,
        );
        ribbons.push(
          <text
            key={`r${i}t2`}
            x={bx}
            y={by + 10}
            textAnchor="middle"
            fontSize={10.5}
            fill={TONE.muted}
          >
            {g.bypassSub ?? (i === 1 ? t('river.bypassSubLodging') : '')}
          </text>,
        );
      }
    } else if (hBypNext > 0.5) {
      // INTO a bypassed gate: the flow splits — the part the gate is about enters
      // it, the rest peels off below as the grey bypass band.
      const hNeed = Math.max(0, Math.min(hOut, hInNext));
      ribbons.push(<path key={`r${i}`} d={segPath(xa, xb, Y0, hOut, Y0, hNeed)} fill={fill} />);
      if (g.outFill === 'hatch' && hSol > 0.5)
        ribbons.push(
          <path
            key={`r${i}s`}
            d={segPath(xa, xb, Y0, hSol, Y0, Math.min(hSol, hNeed))}
            fill={TONE.done}
          />,
        );
      ribbons.push(
        <path key={`r${i}b`} d={segPath(xa, xb, Y0 + hOut, 0, Y0 + hNeed, hBypNext)} fill={TONE.line} />,
      );
    } else {
      ribbons.push(
        <path
          key={`r${i}`}
          d={segPath(xa, xb, Y0, hOut, Y0, hInNext > 0.5 ? Math.min(hOut, hInNext) : hOut)}
          fill={fill}
        />,
      );
      if (g.outFill === 'hatch' && hSol > 0.5)
        ribbons.push(
          <path key={`r${i}s`} d={segPath(xa, xb, Y0, hSol, Y0, hSol)} fill={TONE.done} />,
        );
    }
    if (model.runningGate === i + 1 && hOut > 0.5) {
      ribbons.push(
        <path
          key={`traffic${i}`}
          d={segPath(xa, xb, Y0, hOut, Y0, hOut)}
          fill="url(#tower-traffic)"
        />,
      );
      ribbons.push(
        <path
          key={`dash${i}`}
          className="tower-dash"
          d={`M${xa},${Y0 + hOut / 2} L${xb},${Y0 + hOut / 2}`}
          stroke={TONE.ink}
          strokeWidth={2}
          fill="none"
          strokeDasharray="8 8"
          opacity={0.7}
        />,
      );
    }
  }

  // end pools
  const endEls: ReactElement[] = [];
  const in5 = (v.in5 ?? 0) * sc;
  if (in5 > 0.5 || (v.travelled ?? 0) + (v.ready ?? 0) > 0.5) {
    const ex = X[5]! + GW + 8;
    const ew = W - ex - 6;
    let ey = Y0;
    const tr = (v.travelled ?? 0) * sc;
    const rd = (v.ready ?? 0) * sc;
    const hT = Math.max(26, tr);
    const hR = Math.max(26, rd);
    if ((v.travelled ?? 0) > 0.5) {
      const c = poolColors('ok');
      endEls.push(
        <path key="e0s" d={segPath(X[5]! + GW, ex, Y0, tr, ey, hT)} fill={TONE.ok} opacity={0.5} />,
      );
      endEls.push(
        <g
          key="e0"
          onClick={() => onTarget({ view: 'passengers' })}
          className="cursor-pointer"
          role="button"
          tabIndex={0}
        >
          <title>{t('river.end.travelledTitle', { n: fmt(v.travelled ?? 0) })}</title>
          <rect x={ex} y={ey} width={ew} height={hT} rx={6} fill={c.fill} stroke={c.stroke} />
          <text
            x={ex + 8}
            y={ey + hT / 2 + 1}
            fontSize={13}
            fontWeight={700}
            fill={c.text}
            dominantBaseline="middle"
          >
            {fmt(v.travelled ?? 0)}
          </text>
          <text
            x={ex + 8 + fmt(v.travelled ?? 0).length * 8.2 + 6}
            y={ey + hT / 2 + 1}
            fontSize={11}
            fill="hsl(var(--foreground) / 0.8)"
            dominantBaseline="middle"
          >
            {t('river.end.travelled')}
          </text>
        </g>,
      );
      ey += hT + 8;
    }
    if ((v.ready ?? 0) > 0.5) {
      // Issued but not flown yet: a WAITING pool (pale amber, dashed) — it never
      // reads as "done"; only travelled groups pass the Departure gate.
      const c = poolColors('pend');
      endEls.push(
        <path
          key="e1s"
          d={segPath(X[5]! + GW, ex, Y0 + tr, rd, ey, hR)}
          fill={TONE.pend}
          opacity={0.35}
        />,
      );
      endEls.push(
        <g
          key="e1"
          onClick={() => onTarget({ view: 'passengers' })}
          className="cursor-pointer"
          role="button"
          tabIndex={0}
        >
          <title>{t('river.end.readyTitle', { n: fmt(v.ready ?? 0) })}</title>
          <rect
            x={ex}
            y={ey}
            width={ew}
            height={hR}
            rx={6}
            fill={c.fill}
            stroke={c.stroke}
            strokeDasharray="4 3"
          />
          <text
            x={ex + 8}
            y={ey + hR / 2 + 1}
            fontSize={13}
            fontWeight={700}
            fill={c.text}
            dominantBaseline="middle"
          >
            {fmt(v.ready ?? 0)}
          </text>
          <text
            x={ex + 8 + fmt(v.ready ?? 0).length * 8.2 + 6}
            y={ey + hR / 2 + 1}
            fontSize={10.5}
            fill={c.text}
            dominantBaseline="middle"
          >
            {model.end.readyLabel}
          </text>
        </g>,
      );
    }
  }

  return (
    <div
      ref={boxRef}
      className={cn('relative h-full min-h-0 w-full', className)}
      data-testid="tower-river"
    >
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="block h-full w-full"
        preserveAspectRatio="xMidYMin meet"
        aria-label={t('river.aria')}
      >
        <defs>
          <pattern
            id="tower-hatch"
            width="6"
            height="6"
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(45)"
          >
            <rect width="6" height="6" fill={TONE.surface} />
            <rect width="3" height="6" fill={TONE.ink} />
          </pattern>
          <pattern
            id="tower-traffic"
            width="18"
            height="18"
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(45)"
          >
            <rect width="18" height="18" fill="transparent" />
            <rect width="6" height="18" fill={TONE.surface} opacity={0.35} />
            {reduceMotion ? null : (
              <animateTransform
                attributeName="patternTransform"
                type="translate"
                from="0 0"
                to="18 0"
                dur="0.9s"
                repeatCount="indefinite"
                additive="sum"
              />
            )}
          </pattern>
          <pattern
            id="tower-hatch-grey"
            width="6"
            height="6"
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(45)"
          >
            <rect width="6" height="6" fill={TONE.surface} />
            <rect width="3" height="6" fill={TONE.line} />
          </pattern>
          <style>{`.tower-dash{animation:tower-dash 1.2s linear infinite}@keyframes tower-dash{to{stroke-dashoffset:-32}}.tower-pulse{animation:tower-pulse 1.6s ease-in-out infinite}@keyframes tower-pulse{50%{opacity:.35}}@media (prefers-reduced-motion: reduce){.tower-dash,.tower-pulse{animation:none}}`}</style>
        </defs>
        {els}
        {bandEls}
        <g opacity={dimAll ? 0.45 : 1}>{ribbons}</g>
        {highlights}
        {gateEls}
        {endEls}
        {labels}
        {!model.known ? (
          <text x={W / 2} y={Y0 + HMAX / 2} textAnchor="middle" fontSize={12} fill={TONE.muted}>
            {t('river.notLoaded')}
          </text>
        ) : null}
        <text x={10} y={H - 8} fontSize={10.5} fill={TONE.muted}>
          {t('river.footer')}
        </text>
      </svg>
      <div
        className={cn(
          'pointer-events-none absolute bottom-6 right-5 grid h-[84px] w-[84px] -rotate-6 place-items-center rounded-full border-[3px] text-center text-[11px] font-bold leading-tight',
          model.end.contained
            ? 'border-[color:var(--status-ok,#1F8A5B)] bg-[color:var(--status-ok-soft,#DFF3E8)] text-[color:var(--status-ok,#1F8A5B)]'
            : 'border-border text-muted-foreground/60',
        )}
        aria-label={model.end.contained ? t('river.contained') : t('river.notContained')}
      >
        <span>
          {t('river.containedStamp')}
          <br />
          <span className="font-mono text-[10px] font-medium">
            {model.end.contained && model.end.containedAt
              ? new Date(model.end.containedAt).toLocaleTimeString(undefined, {
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : '—'}
          </span>
        </span>
      </div>
    </div>
  );
}

function gateTarget(g: RiverGate): TowerTarget {
  switch (g.op) {
    case 'hotels':
      return { view: 'hotels' };
    case 'transfers':
      return { view: 'hotels', transfer: 'PLAN' };
    case 'contain':
      return { view: 'board' };
    default:
      return { view: 'passengers' };
  }
}
